import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { SiteContentProvider, useSiteContent } from "./content.js";
import { MAIN_HOST, SWITCHED_TO_GP85 } from "./siteAddress.js";
import {
  ScrollProgress,
  useActiveSection,
  useHashScroll,
  useSectionInView,
} from "./components/primitives.jsx";
import Nav from "./components/nav.jsx";
import Hero from "./components/hero.jsx";
import BoothSection from "./components/booth.jsx";
import Book from "./components/book.jsx";
import Memorabilia from "./components/memorabilia.jsx";
import Gallery from "./components/gallery.jsx";
import Watch from "./components/watch.jsx";
import Podcasts from "./components/podcasts.jsx";
import About from "./components/about.jsx";
import Apply from "./components/apply.jsx";
import MyTake from "./components/mytake.jsx";
import AmaTeaser from "./components/ama-teaser.jsx";
import Footer from "./components/footer.jsx";
import CheckoutBanner from "./components/checkout-banner.jsx";
import StatsPage from "./components/stats.jsx";
// The Yankees story and the La Russa foreword are just text + a little CSS, and
// they're the homepage's primary engagement overlays. They ride in the main
// bundle (static imports) so opening either is instant — no lazy chunk, no
// Suspense, no preload, no spinner. Each module's default export is the
// standalone page; the named export is the in-app modal.
import ShavePage, { ShaveModal } from "./components/shave.jsx";
import ForewordPage, { ForewordModal } from "./components/foreword.jsx";
import { useMeta } from "./seo.js";
import { ROUTE_META } from "./routeMeta.js";

// Admin and the privacy policy never appear on the homepage — load them on
// demand so the homepage bundle doesn't ship the admin console or the legal
// page. Each renders behind its own <Suspense> below.
//
// StatsPage stays static on purpose: the homepage already embeds StatsExplorer
// from this same module (and the footer pulls ABOUT_THE_DATA_TEXT from it), so
// stats.jsx can't be lifted out of the main chunk — a lazy() here would just be
// a dynamic import that never actually splits.
const Admin = lazy(() => import("./components/admin.jsx"));
const Privacy = lazy(() => import("./components/privacy.jsx"));
// The AMA transcript is ~50 threads of text plus its own styling — a page
// visitors reach deliberately, never needed by the homepage. Lazy like Admin.
const AmaPage = lazy(() => import("./components/ama.jsx"));
// Greg's live speaking deck — a presenting tool reached by URL, not from the
// nav. Lazy for the same reason as Admin: the homepage never needs it.
const Presentation = lazy(() => import("./components/presentation.jsx"));
// Greg's live booth (/live, plus /live/thanks after a shoutout payment) —
// streaming player, live game data, shoutouts. Its own chunk: the homepage
// only links to it from the nav.
const LivePage = lazy(() => import("./components/live.jsx"));
// The show's pages (the site as it will be on gp85.com): /gp85, /gp85/album,
// /gp85/games/<night>, and the root once the move is made. Their own chunk.
const Gp85 = lazy(() => import("./components/gp85/page.jsx"));

// The story lives at a real, indexable URL but opens as an in-app overlay when
// reached from inside the site (no reload), and as a full page on a direct
// visit / reload / shared link / crawler.
const SHAVE_PATH = "/the-day-the-yankees-made-me-shave";
const FOREWORD_PATH = "/foreword-by-tony-la-russa";

// Pathname state + the one in-app navigation we do without a reload: opening
// and closing the story overlay. The Worker serves index.html for any
// non-asset, non-API request, so /privacy hits this app and we render the right
// page. Keeping the URL as the single source of truth means browser back/forward
// just work — back closes the overlay, forward reopens it.
function useRouter() {
  const [path, setPath] = useState(typeof window !== "undefined" ? window.location.pathname : "/");
  useEffect(() => {
    const onPop = () => setPath(window.location.pathname);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  // `state` rides on the history entry (the album uses it to know what it
  // pushed, so closing walks back over exactly that).
  const push = (to, state = null) => {
    window.history.pushState(state, "", to);
    setPath(to);
  };
  const replace = (to, state = null) => {
    window.history.replaceState(state, "", to);
    setPath(to);
  };
  return { path, push, replace, back: () => window.history.back() };
}

// Shown for the instant a lazy route chunk is in flight. Full-viewport navy so
// switching routes never flashes a white screen before the view paints.
function RouteFallback() {
  return <div style={{ minHeight: "100vh", background: "var(--navy-900)" }} aria-hidden />;
}

export default function App() {
  const { path, push, replace, back } = useRouter();
  // Render the standalone story page only when its URL is where the app first
  // loaded (direct visit / reload / shared link / crawler). Otherwise the story
  // is the overlay over the homepage, which stays mounted underneath so closing
  // returns the reader to the exact spot they left.
  const enteredOnStory = useRef(
    typeof window !== "undefined" && window.location.pathname.startsWith(SHAVE_PATH),
  );
  const openStory = () => push(SHAVE_PATH);
  const closeStory = () => {
    if (window.location.pathname.startsWith(SHAVE_PATH)) back();
  };
  // The La Russa foreword follows the exact same pattern as the shave story:
  // a real, indexable URL that opens as an in-app overlay from inside the site
  // and as a full page on a direct visit / reload / shared link / crawler.
  const enteredOnForeword = useRef(
    typeof window !== "undefined" && window.location.pathname.startsWith(FOREWORD_PATH),
  );
  const openForeword = () => push(FOREWORD_PATH);
  const closeForeword = () => {
    if (window.location.pathname.startsWith(FOREWORD_PATH)) back();
  };

  if (path.startsWith("/admin")) {
    return (
      <Suspense fallback={<RouteFallback />}>
        <Admin />
      </Suspense>
    );
  }
  if (path.startsWith("/privacy")) {
    return (
      <Suspense fallback={<RouteFallback />}>
        <Privacy />
      </Suspense>
    );
  }
  if (path.startsWith(SHAVE_PATH)) {
    if (enteredOnStory.current) {
      // Direct entry — the story is the page's primary content.
      return <ShavePage />;
    }
    // Reached from inside the site — overlay it on the still-mounted homepage.
    return (
      <SiteContentProvider>
        <Home onOpenStory={openStory} onOpenForeword={openForeword} />
        <ShaveModal onClose={closeStory} />
      </SiteContentProvider>
    );
  }
  if (path.startsWith(FOREWORD_PATH)) {
    if (enteredOnForeword.current) {
      // Direct entry — the foreword is the page's primary content.
      return <ForewordPage />;
    }
    // Reached from inside the site — overlay it on the still-mounted homepage.
    return (
      <SiteContentProvider>
        <Home onOpenStory={openStory} onOpenForeword={openForeword} />
        <ForewordModal onClose={closeForeword} />
      </SiteContentProvider>
    );
  }
  // /buy is the shareable "just let me buy the book" link. It renders the
  // homepage with the checkout modal already open: the path is rewritten to
  // /?buy=1 (preserving any other query params, e.g. utm tags) and the Book
  // section reads that param on mount and pops the modal. The worker serves
  // this path with its own social-card metadata — see routeMeta.js.
  if (path === "/buy" || path.startsWith("/buy/")) {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      params.set("buy", "1");
      window.history.replaceState(null, "", `/?${params.toString()}`);
    }
    if (SWITCHED_TO_GP85) {
      return (
        <SiteContentProvider>
          <Suspense fallback={<RouteFallback />}>
            <Gp85 path="/" push={push} replace={replace} />
          </Suspense>
        </SiteContentProvider>
      );
    }
    return (
      <SiteContentProvider>
        <Home onOpenStory={openStory} onOpenForeword={openForeword} />
      </SiteContentProvider>
    );
  }
  // /bonus-stories is no longer a public route. The reader now lives inside
  // /admin so only Greg sees it. Anyone with the old URL gets redirected to
  // the homepage rather than 404, so legacy links don't dead-end.
  if (path.startsWith("/bonus-stories")) {
    if (typeof window !== "undefined") {
      window.history.replaceState(null, "", "/");
    }
    if (SWITCHED_TO_GP85) {
      return (
        <SiteContentProvider>
          <Suspense fallback={<RouteFallback />}>
            <Gp85 path="/" push={push} replace={replace} />
          </Suspense>
        </SiteContentProvider>
      );
    }
    return (
      <SiteContentProvider>
        <Home onOpenStory={openStory} onOpenForeword={openForeword} />
      </SiteContentProvider>
    );
  }
  if (path.startsWith("/stats")) {
    return (
      <SiteContentProvider>
        <StatsPage />
      </SiteContentProvider>
    );
  }
  if (path.startsWith("/presentation")) {
    return (
      <Suspense fallback={<RouteFallback />}>
        <Presentation />
      </Suspense>
    );
  }
  if (path === "/live" || path.startsWith("/live/")) {
    return (
      <SiteContentProvider>
        <Suspense fallback={<RouteFallback />}>
          <LivePage />
        </Suspense>
      </SiteContentProvider>
    );
  }
  if (path === "/ama" || path.startsWith("/ama/")) {
    return (
      <SiteContentProvider>
        <Suspense fallback={<RouteFallback />}>
          <AmaPage />
        </Suspense>
      </SiteContentProvider>
    );
  }
  // The show's pages. Until the move to gp85.com they're a preview under
  // /gp85; after it, "/" is the show's home and the nights live at /games/…
  if (
    path === "/gp85" ||
    path.startsWith("/gp85/") ||
    (SWITCHED_TO_GP85 &&
      (path === "/" || path === "" || path === "/album" || path.startsWith("/games/")))
  ) {
    return (
      <SiteContentProvider>
        <Suspense fallback={<RouteFallback />}>
          <Gp85 path={path} push={push} replace={replace} />
        </Suspense>
      </SiteContentProvider>
    );
  }
  // Exact "/" is the homepage. Anything else didn't match a known route —
  // the Worker has already returned a 404 status; we just need to render a
  // friendly body. Without this branch, an unknown URL would silently
  // render the homepage and confuse the visitor.
  const isHomepagePath = path === "/" || path === "" || path === "/index.html";
  if (!isHomepagePath) {
    return (
      <SiteContentProvider>
        <NotFound path={path} />
      </SiteContentProvider>
    );
  }
  return (
    <SiteContentProvider>
      <Home onOpenStory={openStory} onOpenForeword={openForeword} />
    </SiteContentProvider>
  );
}

function NotFound({ path }) {
  useMeta({ ...ROUTE_META.notFound, path });
  return (
    <main className="notfound-page grain">
      <div className="notfound-inner">
        <p className="mono notfound-kicker">404 · OFF THE BACK OF THE CARD</p>
        <h1 className="display notfound-title">No play at this base.</h1>
        <p className="notfound-copy">
          Greg's not throwing to first on this one — the URL <code>{path}</code> isn't a page on the
          site.
        </p>
        <a className="notfound-cta" href="/">
          ← Back to {MAIN_HOST}
        </a>
      </div>
    </main>
  );
}

const notFoundStyles = typeof document !== "undefined" ? document.createElement("style") : null;
if (notFoundStyles) {
  notFoundStyles.textContent = `
.notfound-page {
  min-height: 100vh;
  display: grid;
  place-items: center;
  padding: 80px 24px;
  background: var(--navy-900);
  color: var(--bone);
}
.notfound-inner { max-width: 640px; text-align: center; }
.notfound-kicker {
  color: var(--gold);
  font-size: 0.6875rem;
  letter-spacing: 0.22em;
  margin: 0 0 28px;
}
.notfound-title {
  font-size: clamp(2.5rem, 8vw, 5rem);
  line-height: 1.05;
  color: var(--bone);
  margin: 0 0 28px;
}
.notfound-copy {
  font-family: var(--serif);
  font-size: 1.125rem;
  line-height: 1.6;
  color: var(--bone-dim);
  margin: 0 0 40px;
}
.notfound-copy code {
  font-family: var(--mono);
  font-size: 0.875rem;
  padding: 2px 8px;
  background: rgba(189, 155, 96, 0.12);
  border: 1px solid rgba(189, 155, 96, 0.32);
  border-radius: 4px;
  color: var(--gold-bright);
  word-break: break-all;
}
.notfound-cta {
  display: inline-block;
  padding: 14px 24px;
  border: 1px solid var(--gold);
  background: transparent;
  color: var(--bone);
  font-family: var(--mono);
  font-size: 0.75rem;
  letter-spacing: 0.16em;
  text-decoration: none;
  border-radius: 6px;
  transition: background 200ms, color 200ms;
}
.notfound-cta:hover {
  background: var(--gold);
  color: var(--navy-900);
}
`;
  document.head.appendChild(notFoundStyles);
}

function Home({ onOpenStory, onOpenForeword }) {
  const content = useSiteContent();
  const ids = content.nav.items.map((n) => n.id);
  const active = useActiveSection(ids);
  useSectionInView();
  useHashScroll();
  // Explicit homepage SEO. This re-asserts the values baked into index.html
  // so navigating from /privacy or /stats back to / restores the canonical
  // homepage metadata cleanly.
  useMeta(ROUTE_META.home);

  return (
    <>
      <ScrollProgress />
      <CheckoutBanner />
      <Nav active={active} />
      <a href="#main" className="skip-link">
        Skip to main content
      </a>
      <main id="main" className="grain">
        <Hero />
        <BoothSection />
        <Book onOpenStory={onOpenStory} onOpenForeword={onOpenForeword} />
        <About show={["intro"]} />
        <Memorabilia />
        <Gallery />
        <Watch />
        <Podcasts />
        <AmaTeaser />
        <About show={["speaking"]} />
        <Apply />
        <MyTake />
        <About show={["health"]} />
        <About show={["facebook"]} />
        <About show={["contact"]} />
      </main>
      <Footer />
    </>
  );
}
